import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthService } from '@mes-slalom/ui-common-lib';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ToastMessageService } from './toast-message.service';

@Injectable({ providedIn: 'root' })
export class ApiService {
  protected baseUrl = environment.apiUrl;
  private readonly toastService = inject(ToastMessageService);

  constructor(protected http: HttpClient, private auth: AuthService) {}

  private handleError(error: unknown, endpoint: string): never {
    if (error instanceof HttpErrorResponse) {
      const status = error.status;
      const message = this.extractErrorMessage(error);

      if (status === 0) {
        this.toastService.error(
          'Unable to reach the server. If the problem persists, please contact support.',
          'Unknown Error'
        );
      } else if (status === 401) {
        console.error(`Unauthorized access to ${endpoint}:`, error);
      } else if (status === 403) {
        this.toastService.error(
          'You do not have permission to perform this action.',
          'Access Denied'
        );
      } else if (status === 404) {
        this.toastService.warning(
          'The requested resource could not be found.',
          'Not Found'
        );
      } else if (status === 409) {
        this.toastService.warning(
          message ||
            'A conflict occurred. The resource may have been modified.',
          'Conflict'
        );
      } else if (status === 422) {
        this.toastService.warning(
          message || 'The submitted data is invalid. Please check your input.',
          'Validation Error'
        );
      } else if (status >= 400 && status < 500) {
        this.toastService.error(
          message || 'There was a problem with your request.',
          'Request Error'
        );
      } else if (status >= 500) {
        this.toastService.error(
          'A server error occurred. Please try again later.',
          'Server Error'
        );
        console.error(`Server error at ${endpoint}:`, error);
      }
    } else {
      this.toastService.error(
        'An unexpected error occurred. Please try again.',
        'Error'
      );
      console.error(`Unexpected error at ${endpoint}:`, error);
    }

    throw error;
  }

  private extractErrorMessage(error: HttpErrorResponse): string | null {
    if (typeof error.error === 'string') {
      return error.error;
    }
    if (error.error?.message) {
      return error.error.message;
    }
    if (error.error?.error) {
      return error.error.error;
    }
    if (error.message) {
      return error.message;
    }
    return null;
  }

  // Exposed to subclasses so they can customize headers (e.g. remove JSON content-type for file uploads)
  protected async getAuthHeaders(): Promise<HttpHeaders> {
    const token = await this.auth.getValidAccessToken();
    return new HttpHeaders({
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
      'Correlation-Object': JSON.stringify({
        correlationId: crypto.randomUUID(),
      }),
    });
  }

  async get<T>(endpoint: string, query?: any): Promise<T> {
    try {
      const headers = await this.getAuthHeaders();
      return await firstValueFrom(
        this.http.get<T>(`${this.baseUrl}/${endpoint}`, {
          headers,
          params: query,
        })
      );
    } catch (error) {
      return this.handleError(error, endpoint);
    }
  }

  async post<T>(endpoint: string, body: any): Promise<T> {
    try {
      const headers = await this.getAuthHeaders();
      return await firstValueFrom(
        this.http.post<T>(`${this.baseUrl}/${endpoint}`, body, { headers })
      );
    } catch (error) {
      return this.handleError(error, endpoint);
    }
  }

  async postFormData<T>(endpoint: string, form: FormData): Promise<T> {
    try {
      const baseHeaders = await this.getAuthHeaders();
      const headers = baseHeaders.delete('Content-Type');
      return await firstValueFrom(
        this.http.post<T>(`${this.baseUrl}/${endpoint}`, form, { headers })
      );
    } catch (error) {
      return this.handleError(error, endpoint);
    }
  }

  async put<T>(endpoint: string, body: any): Promise<T> {
    try {
      const headers = await this.getAuthHeaders();
      return await firstValueFrom(
        this.http.put<T>(`${this.baseUrl}/${endpoint}`, body, { headers })
      );
    } catch (error) {
      return this.handleError(error, endpoint);
    }
  }

  async delete<T>(endpoint: string): Promise<T> {
    try {
      const headers = await this.getAuthHeaders();
      return await firstValueFrom(
        this.http.delete<T>(`${this.baseUrl}/${endpoint}`, { headers })
      );
    } catch (error) {
      return this.handleError(error, endpoint);
    }
  }
}
